import axiosInstance from '../utils/axios';

const getV2Base = () => '/api/v2/recommendations';

const NUTRIENT_CODES = ['oc', 'p', 'k', 'n', 'ph', 's', 'ca', 'mg', 'zn', 'b', 'cu', 'fe'];

export const getStates = async () => {
  const res = await axiosInstance.get(`${getV2Base()}/states`);
  return res.data?.data || [];
};

export const getLevelsForCode = (code) => {
  if (code === 'ph') return ['Acidic', 'neutral', 'Alkaline'];
  return ['low', 'medium', 'high'];
};

export const getConstants = () =>
  Promise.resolve({
    nutrientCodes: NUTRIENT_CODES,
    getLevelsForCode
  });

export const listCrops = async ({ search = '', state_id = '' } = {}) => {
  const params = {};
  if (search) params.search = search;
  if (state_id) params.state_id = state_id;

  const res = await axiosInstance.get(`${getV2Base()}/crops`, { params });
  const data = res.data?.data || [];
  return {
    crops: data.map((c) => ({
      id: c.id,
      slug: c.code,
      name_en: c.name_en,
      is_v2_enabled: true,
    })),
  };
};

export const getCrop = async (slug) => {
  const res = await axiosInstance.get(
    `${getV2Base()}/${encodeURIComponent(slug)}/master`
  );
  const api = res.data || {};
  const data = api.data || {};

  const stages = (data.stages || []).map((s) => ({
    id: s.id,
    order: s.order,
    order_name_i18n: s.orderName || {},
  }));

  const prescriptions = [];
  NUTRIENT_CODES.forEach((code) => {
    const byCode = data[code];
    if (!byCode) return;
    const levels = getLevelsForCode(code);
    levels.forEach((level) => {
      const rec = byCode[level];
      if (!rec) return;
      if (Array.isArray(rec)) {
        rec.forEach((rec) => {
          prescriptions.push({
            id: rec.id,
            nutrient_code: code,
            level,
            input_i18n: rec.input || {},
            constant_dose: !!rec.constantDose,
            dose: rec.dose ?? '',
            per_session_dose: rec.perSessionDose ?? '',
            per_session_dose_is_array: false,
            state_id: rec.state_id || '',
            state_name: rec.state_name || 'All States',
            min_age: rec.min_age !== null ? rec.min_age : '',
            max_age: rec.max_age !== null ? rec.max_age : '',
            unit_i18n: rec.unit || {},
            method_i18n: rec.method || {},
            stage_indices: Array.isArray(rec.stage) ? rec.stage : (rec.stage ? [rec.stage] : []),
          });
        });
      }
    });
  });

  const all = (Array.isArray(data.all) ? data.all : []).map((rec, index) => ({
    id: rec.id || (index + 1),
    input_i18n: rec.input || {},
    constant_dose: !!rec.constantDose,
    dose: rec.dose ?? '',
    per_session_dose: rec.perSessionDose ?? '',
    per_session_dose_is_array: Array.isArray(rec.perSessionDose),
    state_id: rec.state_id || '',
    state_name: rec.state_name || 'All States',
    min_age: rec.min_age !== null ? rec.min_age : '',
    max_age: rec.max_age !== null ? rec.max_age : '',
    unit_i18n: rec.unit || {},
    method_i18n: rec.method || {},
    stage_indices: Array.isArray(rec.stage)
      ? rec.stage
      : (rec.stage ? [rec.stage] : []),
  }));

  return {
    crop: {
      id: api.crop,
      slug: api.crop,
      name_en: api.name_en,
      is_v2_enabled: true,
      stages,
      prescriptions,
      all,
    },
  };
};

export const createCrop = async ({ slug }) => {
  const res = await axiosInstance.post(`${getV2Base()}/crops`, { slug });
  return { crop: { id: res.data.crop.id, slug: res.data.crop.code, is_v2_enabled: true } };
};

export const updateCrop = async (slug, body) => {
  const res = await axiosInstance.put(
    `${getV2Base()}/crops/${encodeURIComponent(slug)}`,
    body
  );
  const crop = res.data.crop;
  return { crop: { id: crop.id, slug: crop.code, is_v2_enabled: true } };
};

export const deleteCrop = async (slug) => {
  await axiosInstance.delete(`${getV2Base()}/crops/${encodeURIComponent(slug)}`);
};

export const putStages = async (slug, stages) => {
  const res = await axiosInstance.put(
    `${getV2Base()}/${encodeURIComponent(slug)}/stages`,
    stages
  );
  return {
    stages: (res.data.stages || []).map((s) => ({
      id: s.id,
      order: s.order,
      order_name_i18n: s.orderName || {},
    })),
  };
};

export const upsertPrescription = async (slug, nutrientCode, level, body) => {
  await axiosInstance.put(
    `${getV2Base()}/${encodeURIComponent(
      slug
    )}/prescriptions/${encodeURIComponent(nutrientCode)}/${encodeURIComponent(
      level
    )}`,
    body
  );
};

export const deletePrescription = async (slug, nutrientCode, level, id) => {
  await axiosInstance.delete(
    `${getV2Base()}/${encodeURIComponent(slug)}/prescriptions/${encodeURIComponent(nutrientCode)}/${encodeURIComponent(level)}/${encodeURIComponent(id)}`
  );
};

export const createAllItem = async (slug, body) => {
  await axiosInstance.post(
    `${getV2Base()}/${encodeURIComponent(slug)}/all`,
    body
  );
};

export const updateAllItem = async (slug, id, body) => {
  await axiosInstance.put(
    `${getV2Base()}/${encodeURIComponent(slug)}/all/${encodeURIComponent(id)}`,
    body
  );
};

export const deleteAllItem = async (slug, id) => {
  await axiosInstance.delete(
    `${getV2Base()}/${encodeURIComponent(slug)}/all/${encodeURIComponent(id)}`
  );
};

// ─── Fertilizer Formula Configs ──────────────────────────────────────────────

export const getFormulaConfigs = async (slug) => {
  const res = await axiosInstance.get(
    `${getV2Base()}/${encodeURIComponent(slug)}/formulas`
  );
  return res.data?.data || [];
};

export const upsertFormulaConfig = async (slug, fertilizerType, body) => {
  const res = await axiosInstance.put(
    `${getV2Base()}/${encodeURIComponent(slug)}/formulas/${encodeURIComponent(fertilizerType)}`,
    body
  );
  return res.data?.data || null;
};

export const deleteFormulaConfig = async (slug, fertilizerType) => {
  await axiosInstance.delete(
    `${getV2Base()}/${encodeURIComponent(slug)}/formulas/${encodeURIComponent(fertilizerType)}`
  );
};
